import React, { useMemo, useEffect, useContext, useState, useCallback, useRef } from "react";
import { createColumnHelper } from "@tanstack/react-table";
import BasicModal from "components/ui/BasicModal";
import TableBasic from "components/ui/Table";
import BasicButton from "components/forms/BasicButton";
import { Badge } from "theme-ui";
import { DiscountIcon, ModalContent } from "./styled";
import { CheckMarkIcon, WarningIcon } from "../../../components/svg";
import { themevals } from "theme/themevals";
import { trackActivity } from "api/ActivityTracker";
import UserContext from "context/User";
import { TimesIcon, SubmitIcon, PlaneIcon, PartyIcon } from "components/svg";


interface Project {
  projectNumber: string;
  projectId: string;
  quoteId: string;
  currentTotal: string;
  discount5Total: string;
  discount3Total: string;
}

interface DiscountInfoModalProps {
  isOpen: boolean;
  onClose: () => void;
  onViewOrPayInvoice: (projectNumber: string, quoteId: string, projectId: string) => void;
  onSendInvoice: (projectNumber: string, quoteId: string) => void;
  onDontRemindMe: (quoteId: string) => void;
  projects: Project[];

  submittingSendQuoteIds: Set<string>;
  billingEmails: Map<string, string>;
  discountPercentage8?: number;
  discountPercentage3?: number;
  depositPercentage100?: number;
  depositPercentage50?: number;
  paymentDeadlineDays?: number;
}

const columnHelper = createColumnHelper<Project>();

const DiscountInfoModal: React.FC<DiscountInfoModalProps> = ({
  isOpen,
  onClose,
  onViewOrPayInvoice,
  onSendInvoice,
  onDontRemindMe,
  projects,
  submittingSendQuoteIds,
  billingEmails,
  discountPercentage8 = 8,
  discountPercentage3 = 3,
  depositPercentage100 = 100,
  depositPercentage50 = 50,
  paymentDeadlineDays = 5,
}) => {
  const { user } = useContext<any>(UserContext);
  const [submittingQuoteIds, setSubmittingQuoteIds] = useState<Set<string>>(new Set());
  const hasTrackedView = useRef(false);

  // Wrapper for onClose with activity tracking
  const handleClose = useCallback(() => {
    
    trackActivity({
      module: "discount",
      action: "discount_popup_cancelled",
      actionType: "click",
      userId: user?.id || null,
      companyId: user?.company?.id || null,
      metadata: {
        projectIds: projects.map(p => p.projectId),
        quoteIds: projects.map(p => p.quoteId),
        projectCount: projects.length
      }
    });

    // Call the original handler
    onClose();
  }, [onClose, user?.id, user?.company?.id, projects]);

  const handleDontRemindMeClick = useCallback(async (quoteId: string) => {
    setSubmittingQuoteIds(prev => new Set(prev).add(quoteId));
    try {
      await onDontRemindMe(quoteId);
    } finally {
      setSubmittingQuoteIds(prev => {
        const next = new Set(prev);
        next.delete(quoteId);
        return next;
      });
    }
  }, [onDontRemindMe]);

  // Wrapper for view/pay invoice with activity tracking
  const handleViewOrPayInvoice = useCallback((projectNumber: string, quoteId: string, projectId: string) => {
    // Track the view activity
    trackActivity({
      module: "invoices",
      action: "view_or_pay_invoice",
      actionType: "view",
      userId: user?.id || null,
      companyId: user?.company?.id || null,
      entityType: "project",
      entityId: projectId,
      metadata: {
        source: "discount_popup",
        projectId,
        quoteId,
      }
    });

    // Call the original handler
    onViewOrPayInvoice(projectNumber, quoteId, projectId);
  }, [onViewOrPayInvoice, user?.id, user?.company?.id]);

  // Wrapper for send invoice with activity tracking
  const handleSendInvoice = useCallback((projectNumber: string, quoteId: string, projectId: string) => {
    // Track the send activity
    trackActivity({
      module: "discount",
      action: "invoice_sent_from_discount_modal",
      actionType: "click",
      userId: user?.id || null,
      companyId: user?.company?.id || null,
      entityType: "project",
      entityId: projectId,
      metadata: {
        projectNumber,
        quoteId,
        source: "discount_modal"
      }
    });

    // Call the original handler
    onSendInvoice(projectNumber, quoteId);
  }, [onSendInvoice, user?.id, user?.company?.id]);

  // Track when the modal is viewed (only once per modal opening)
  useEffect(() => {
    if (isOpen && projects.length > 0 && !hasTrackedView.current) {
      hasTrackedView.current = true;
      const projectIds = projects.map(project => project.projectId);
      console.log('user:', user);
      trackActivity({
        module: "discount",
        action: "discount_popup_viewed",
        actionType: "view",
        userId: user?.id || null,
        companyId: user?.company?.id || null,
        metadata: {
          projectIds: projectIds,
          projectCount: projects.length
        }
      });
    }
    
    // Reset the tracking flag when modal closes
    if (!isOpen) {
      hasTrackedView.current = false;
    }
  }, [isOpen, projects, user?.id, user?.companyId]);

  const columns = useMemo(
    () => [
      columnHelper.accessor("projectNumber", {
        header: "Project #",
        cell: (info) => {
          const value = info.getValue();
          const projectId = info.row.original.projectId;
          // Format as GMP-##### (e.g., GMP-00001)
          const numValue = value.replace(/\D/g, '');
          const formatted = `GMP-${numValue.padStart(5, '0')}`;
          return (
            <a 
              href={`/projects/${projectId}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                fontWeight: 600, 
                color: themevals.colors.black,
                textDecoration: 'underline',
                cursor: 'pointer'
              }}
              onMouseEnter={(e) => e.currentTarget.style.color = themevals.colors.black}
              onMouseLeave={(e) => e.currentTarget.style.color = themevals.colors.black}
            >
              {formatted}
            </a>
          );
        },
        meta: {
          style: { textAlign: "left", maxWidth: "150px", width: "150px" },
        },
      }),
      columnHelper.accessor("currentTotal", {
        header: "Current Total",
        cell: (info) => {
          const value = info.getValue();
          return (
            <span style={{fontWeight:600, color:themevals.colors.black}}>
              ${value}
            </span>
          );
        },
        meta: {
          style: { textAlign: "left" },
        },
      }),
      columnHelper.accessor("discount5Total", {
        header: "5% Discount (paid in full)",
        cell: (info) => {
          const value = info.getValue();
          return (
            <span style={{fontWeight:600, color:themevals.colors.success}}>
              ${value}
            </span>
          );
        },
        meta: {
          style: { textAlign: "left" },
        },
      }),
      columnHelper.accessor("discount3Total", {
        header: "3% Discount (50% deposit)",
        cell: (info) => {
          const value = info.getValue();
          return (
            <span style={{fontWeight:600, color:themevals.colors.success}}>
              ${value}
            </span>
          );
        },
        meta: {
          style: { textAlign: "left" },
        },
      }),

      columnHelper.display({
        id: "actions",
        header: "",
        cell: (info) => {
          const quoteId = info.row.original.quoteId;
          const isSending = submittingSendQuoteIds.has(quoteId);
          
          return (
            <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}>
              <BasicButton
                type="button"
                onClick={() => {
                  handleViewOrPayInvoice(info.row.original.projectNumber, quoteId, info.row.original.projectId);
                }}
                scheme="successOutline"
                styles={{ padding: "5px 15px", fontSize: "12px" }}
                keepText={true}
              >
                <SubmitIcon fill={themevals.colors.success} direction="up" />
                View/Pay Invoice
              </BasicButton>
              <BasicButton
                type="button"
                onClick={() => {
                  handleSendInvoice(info.row.original.projectNumber, quoteId, info.row.original.projectId);
                }}
                scheme="infoOutline"
                styles={{ padding: "5px 15px", fontSize: "12px" }}
                $submitting={isSending}
                keepText={true}
                $loadingColor={themevals.colors.info}
              >
                {!isSending && (
                  <PlaneIcon fill={themevals.colors.info}/>
                )}
                Send Invoice to Billing
              </BasicButton>
              <BasicButton
                type="button"
                onClick={() => {
                  handleDontRemindMeClick(quoteId);
                }}
                outline={true}
                scheme="primary"
                styles={{ padding: "5px 15px", fontSize: "12px" }}
                $submitting={submittingQuoteIds.has(quoteId)}
                keepText={true}
                $loadingColor="#000"
              >
                {!submittingQuoteIds.has(quoteId) && (
                  <TimesIcon fill={"#000"}/>
                )}
                Don&apos;t remind me
              </BasicButton>
            </div>
          );
        },
        meta: {
          style: { textAlign: "right" },
        },
      }),
    ],
    [handleViewOrPayInvoice, handleSendInvoice, handleDontRemindMeClick, submittingQuoteIds, submittingSendQuoteIds]
  );

  const icon = (
    <DiscountIcon>
      <svg width="50" height="50" viewBox="0 0 50 50" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect width="50" height="50" rx="6" fill="#F1F2F3" />
        <path
          d="M25 13L28 22H37L30 28L33 37L25 31L17 37L20 28L13 22H22L25 13Z"
          fill="#808080"
        />
      </svg>
    </DiscountIcon>
  );

  if (!isOpen) return null;

  return (
    <BasicModal
      title="Discounts Available!"
      icon={<PartyIcon fill={"#818181"} height="30px"/>}
      size="lg"
      centered={true}
      cancel_click={handleClose}
      cancel_text="Cancel"
    >
      <ModalContent>
        <p style={{ textAlign: "center", marginBottom: "1.5rem", color: "#808080", fontFamily: "Poppins" }}>
          The following projects qualify for discount options if paid within{" "}
          <strong style={{ color: "#000" }}>{paymentDeadlineDays} days</strong> of quote acceptance.
        </p>

        <TableBasic
          data={projects}
          columns={columns}
          enableSearch={false}
          enablePaging={false}
          disableSorting={true}
          noFirstColumnMinWidth={true}
          customStyles={{
            marginBottom: "1rem",
            border: "1px solid var(--theme-ui-colors-base_300)",
          }}
        />
        
        <div style={{ 
          marginBottom: "1.5rem", 
          padding: "0.75rem 1rem", 
          backgroundColor: "#FEF3C7", 
          borderRadius: "8px",
          border: "1px solid #FCD34D"
        }}>
          <div style={{ fontSize: "13px", color: "#92400E", lineHeight: "1.5" }}>
            <strong>Discount Options:</strong> 5% off when you pay in full • 3% off when you pay a 50% deposit (within 5 days of accepting)
          </div>
        </div>
        
        {projects.length > 0 && (
          <div style={{ 
            marginTop: "1.5rem", 
            padding: "1rem", 
            backgroundColor: "#F8F8F8", 
            borderRadius: "8px",
            textAlign: "center"
          }}>
            <div style={{ fontSize: "12px", color: "#808080", marginBottom: "8px" }}>
              Invoices will be sent to:
            </div>
            {Array.from(new Set(projects.map(p => billingEmails.get(p.quoteId)).filter(Boolean))).map((email, idx) => (
              <div key={idx} style={{ fontSize: "14px", fontWeight: 600, color: themevals.colors.black }}>
                {email}
              </div>
            ))}
          </div>
        )}
      </ModalContent>
    </BasicModal>
  );
};

export default DiscountInfoModal;
